import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { communicationApi } from 'api';
import { transformFiltersToQuery } from './utils/transformFilterToQuery';

export const templateSlice = createSlice({
    name: 'template',
    initialState: {
        pending: false,
        savePending: false,
        error: null,
        templates: [],
        defaultTemplates: {},
        count: 0,
        total: 0,
        currentPage: 1,
        totalPages: 1,
        deletePending: false,
        testPending: false,
    },
    reducers: {
        start: (state, action) => {
            state.error = null;
            state.pending = true;
        },
        stop: (state, action) => {
            state.pending = false;
        },
        setError: (state, action) => {
            state.error = action.payload;
            state.pending = false;
        },
        setPending: (state, action) => {
            state.pending = action.payload;
        },
        setTemplates: (state, action) => {
            state.pending = false;
            state.count = action.payload.count;
            state.total = action.payload.total;
            state.templates = action.payload.items;
            state.currentPage = action.payload.currentPage;
            state.totalPages = action.payload.totalPages;
        },
        setDefaultTemplates: (state, action) => {
            const { lang, template, part, data } = action.payload;
            let newValue = _.cloneDeep(state.defaultTemplates);

            if (!newValue[lang]) {
                newValue[lang] = {};
            }
            if (!newValue[lang][template]) {
                newValue[lang][template] = {
                    name: template,
                    lang: lang,
                    text: '',
                    html: '',
                    subject: '',
                };
            }

            newValue[lang][template][part] = data;
            state.defaultTemplates = newValue;
        },
        setSavePending: (state, action) => {
            state.savePending = action.payload;
        },
        addTemplate: (state, action) => {
            state.pending = false;
            state.templates = [action.payload, ...state.templates];
        },
        editTemplate: (state, action) => {
            state.pending = false;
            const index = state.templates.findIndex((templates) => templates.id === action.payload.id);
            if (index !== -1) {
                state.templates = [
                    ...state.templates.slice(0, index),
                    action.payload,
                    ...state.templates.slice(index + 1),
                ];
            }
        },
        deleteTemplate: (state, action) => {
            state.deletePending = false;
            const index = state.templates.findIndex((template) => template.id === action.payload.id);
            if (index !== -1) {
                state.templates = [...state.templates.slice(0, index), ...state.templates.slice(index + 1)];
            }
        },
        startDelete: (state) => {
            state.deletePending = true;
        },
        setTestPending: (state, action) => {
            state.testPending = action.payload;
        },
    },
});

export const {
    start,
    stop,
    setError,
    setTemplates,
    setPending,
    setDefaultTemplates,
    setSavePending,
    addTemplate,
    editTemplate,
    deleteTemplate,
    startDelete,
    setTestPending,
} = templateSlice.actions;

export const getTemplatePending = (state) => state.template.pending;
export const getTestPending = (state) => state.template.testPending;
export const getTemplateError = (state) => state.template.error;
export const getPreviewTemplateDialog = (state) => state.template.previewTemplateDialog;
export const getTemplates = (state) => state.template.templates;
export const getDefaultTemplates = (state) => state.template.defaultTemplates;
export const getSavePending = (state) => state.template.savePending;
export const getTemplatesTotal = (state) => state.template.total;
export const getTemplatesCurrentPage = (state) => state.template.currentPage;
export const getTemplatesTotalPages = (state) => state.template.totalPages;

export const fetchTemplatesAction =
    ({ queryParams, onSuccess, onFailure } = {}) =>
    async (dispatch) => {
        try {
            let query = transformFiltersToQuery(queryParams);
            dispatch(start());
            communicationApi
                .get('templates' + query)
                .then((response) => {
                    dispatch(setTemplates(response.data));
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            dispatch(stop());
            return console.error('error', e);
        }
    };

export const previewTemplateAction = (name, template, part, lang, channel, success) => async (dispatch) => {
    try {
        dispatch(start());
        communicationApi
            .post('templates/preview', {
                templateName: name,
                template,
                part,
                lang,
                channel,
            })
            .then(({ data }) => {
                dispatch(setPending(false));
                success(data);
            })
            .catch((error) => {
                dispatch(setPending(false));
                dispatch(setError(error));
            });
    } catch (e) {
        dispatch(stop());
        return console.error('error', e);
    }
};

export const sendTestTemplateAction =
    ({ templateDto, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(setTestPending(true));
            communicationApi
                .post('templates/test', templateDto)
                .then(({ data }) => {
                    dispatch(setTestPending(false));
                    onSuccess?.(data);
                })
                .catch((error) => {
                    dispatch(setTestPending(false));
                    dispatch(setError(error));
                    onFailure?.(error);
                });
        } catch (e) {
            dispatch(stop());
            onFailure?.(e);
            return console.error('error', e);
        }
    };

export const loadDefaultTemplateAction =
    ({ template, part, lang, channel, success }) =>
    async (dispatch) => {
        try {
            dispatch(start());
            let query = transformFiltersToQuery({
                name: template,
                part,
                lang,
                channel
            });
            communicationApi
                .get('templates/default' + query)
                .then(({ data }) => {
                    dispatch(setDefaultTemplates({ lang, template, part, data: data.template }));
                    dispatch(setPending(false));
                    success(data);
                })
                .catch((error) => {
                    dispatch(setPending(false));
                    dispatch(setError(error));
                });
        } catch (e) {
            dispatch(stop());
            return console.error('error', e);
        }
    };

export const addTemplateAction =
    ({ templateDto, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(start());
            communicationApi
                .post('templates', templateDto)
                .then((response) => {
                    dispatch(addTemplate(response.data));
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(stop());
                    onFailure?.(error.response.data);
                });
        } catch (e) {
            dispatch(stop());
            return console.error(e.message);
        }
    };

export const updateTemplateAction =
    ({ id, templateDto, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            communicationApi
                .put('templates/' + id, templateDto)
                .then((response) => {
                    dispatch(editTemplate(response.data));
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(stop());
                    onFailure?.();
                });
        } catch (e) {
            dispatch(stop());
            return console.error('error', e);
        }
    };

export const deleteTemplateAction =
    ({ id, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(startDelete(id));
            communicationApi
                .delete('templates/' + id)
                .then((response) => {
                    dispatch(deleteTemplate({ id }));
                    onSuccess?.();
                    return response;
                })
                .catch((error) => {
                    onFailure?.(error);
                });
        } catch (e) {
            onFailure?.(e);
            return console.error(e.message);
        }
    };

export default templateSlice.reducer;
